import React, { useEffect, useRef, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  UncontrolledTooltip,
} from "reactstrap"
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaEye,
  FaPaperPlane,
  FaComments,
} from "react-icons/fa"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchPutData,
  FetchPostData,
} from "utils/Common_api_functions"
import { useDataRefresh } from "utils/useDataRefresh"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"
import { socket } from "utils/Connection"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"
import Loader from "components/Loader/loader"

// Most-recently-active query first — same idea as WhatsApp's chat list.
const sortByRecentActivity = list =>
  [...list].sort((a, b) => {
    const aTime = new Date(a.lastMessageAt || a.createdAt).getTime()
    const bTime = new Date(b.lastMessageAt || b.createdAt).getTime()
    return bTime - aTime
  })

const UserQuery = () => {
  const { checkAccess } = usePermissionData()

  const [queries, setQueries] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedQuery, setSelectedQuery] = useState(null)
  const [loading, setLoading] = useState(false)

  // 🔹 Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // 🔹 Chat state
  const adminUser = secureLocalStorage.getItem("admin_user")
  const [chatModalOpen, setChatModalOpen] = useState(false)
  const [chatQuery, setChatQuery] = useState(null)
  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [newMessage, setNewMessage] = useState("")
  const [sendingMessage, setSendingMessage] = useState(false)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    getQueries()
  }, [])

  useDataRefresh(() => {
    getQueries()
  })

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  useEffect(() => {
    const handleIncomingMessage = payload => {
      if (Number(payload?.queryId) !== Number(chatQuery?.id)) return
      setMessages(prev =>
        prev.some(m => m.id === payload?.message?.id)
          ? prev
          : [...prev, payload.message]
      )
    }

    socket.on("queryMessageReceived", handleIncomingMessage)
    return () => socket.off("queryMessageReceived", handleIncomingMessage)
  }, [chatQuery?.id])

  // List-level: bump the query to the top and bump its unread badge when a
  // new message arrives, regardless of whether its chat modal is open.
  useEffect(() => {
    const handleListUpdate = payload => {
      const queryId = Number(payload?.queryId)
      const isEmployeeMessage = payload?.message?.senderType === "employee"
      const isCurrentlyOpen = chatModalOpen && Number(chatQuery?.id) === queryId

      setQueries(prev =>
        sortByRecentActivity(
          prev.map(q => {
            if (Number(q.id) !== queryId) return q
            return {
              ...q,
              lastMessageAt:
                payload?.message?.createdAt || new Date().toISOString(),
              unreadCount:
                isEmployeeMessage && !isCurrentlyOpen
                  ? (q.unreadCount || 0) + 1
                  : q.unreadCount || 0,
            }
          })
        )
      )

      // The query with the new message is sorted to the very top, so bring
      // the admin back to page 1 to see it, same as WhatsApp always showing
      // the newest activity at the top of the visible list.
      setCurrentPage(1)
    }

    socket.on("queryMessageReceived", handleListUpdate)
    return () => socket.off("queryMessageReceived", handleListUpdate)
  }, [chatModalOpen, chatQuery?.id])

  async function getMessages(queryId) {
    const APIdata = await fetchData(
      setMessagesLoading,
      `query/${queryId}/messages`
    )
    if (APIdata?.success) {
      setMessages(APIdata?.messages || [])
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatQuery?.id) return
    const APIdata = await FetchPostData(
      setSendingMessage,
      `query/${chatQuery.id}/messages`,
      { message: newMessage.trim() }
    )
    if (APIdata?.data?.success) {
      const sentMessage = APIdata.data.data
      setMessages(prev => [...prev, sentMessage])
      setNewMessage("")
      setQueries(prev =>
        sortByRecentActivity(
          prev.map(q =>
            q.id === chatQuery.id
              ? { ...q, lastMessageAt: sentMessage.createdAt }
              : q
          )
        )
      )
    } else {
      Swal.fire({
        title: "Failed to send message",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  async function getQueries() {
    const APIdata = await fetchData(setLoading, "query")
    if (APIdata?.success) {
      setQueries(sortByRecentActivity(APIdata?.queries || []))
    } else {
      Swal.fire({
        title: "Error Fetching Queries",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const toggleModal = (query = null) => {
    if (query) {
      const currentQuery = queries.find(q => q.id === query.id)
      setSelectedQuery(currentQuery)
    } else {
      setSelectedQuery(null)
    }
    setModalOpen(!modalOpen)
  }

  const toggleChatModal = (query = null) => {
    if (query) {
      const currentQuery = queries.find(q => q.id === query.id)
      setChatQuery(currentQuery)
      setMessages([])
      setNewMessage("")
      getMessages(currentQuery.id)
      setChatModalOpen(true)
      // Opening the chat marks it read server-side too — clear the badge now.
      setQueries(prev =>
        prev.map(q => (q.id === currentQuery.id ? { ...q, unreadCount: 0 } : q))
      )
    } else {
      setChatQuery(null)
      setMessages([])
      setNewMessage("")
      setChatModalOpen(false)
    }
  }

  const handleCloseQuery = async id => {
    const APIdata = await FetchPutData(setLoading, "query", id, {
      status: true,
    })
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Status Updated", icon: "success" })
      getQueries()
      toggleModal()
    } else {
      Swal.fire({
        title: "Error updating query status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleReopenQuery = async id => {
    const APIdata = await FetchPutData(setLoading, "query", id, {
      status: false,
    })
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Status Updated", icon: "success" })
      getQueries()
      toggleModal()
    } else {
      Swal.fire({
        title: "Error updating query status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // 🔹 Filtered and paginated data
  const filteredQueries = queries.filter(query => {
    const term = searchTerm.toLowerCase()
    return (
      query.employee?.name?.toLowerCase().includes(term) ||
      query.employee?.email?.toLowerCase().includes(term) ||
      query.employee?.phone?.toLowerCase().includes(term) ||
      query.employee?.role?.roleName?.toLowerCase().includes(term) ||
      query.title?.toLowerCase().includes(term) ||
      query.description?.toLowerCase().includes(term)
    )
  })

  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentData = filteredQueries.slice(
    startIndex,
    startIndex + itemsPerPage
  )

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage)
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <PermittedComponent isAccess={checkAccess(7, 1) || checkAccess(7, 2)}>
        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Employee Queries"
          description="View and manage queries submitted by cleaning staff."
        />

        <Card className="shadow-lg">
          <CardBody>
            {/* 🔹 Top Controls */}
            <Row className="align-items-center mb-3">
              <Col md="4">
                <Input
                  type="text"
                  placeholder="Search by name, email, role or title..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </Col>
              <Col md="8" className="text-end">
                <div className="d-inline-flex align-items-center">
                  <Input
                    type="select"
                    value={itemsPerPage}
                    onChange={e => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    style={{ width: "100px", display: "inline-block" }}
                  >
                    <option value={10}>Show 10</option>
                    <option value={20}>Show 20</option>
                    <option value={30}>Show 30</option>
                    <option value={40}>Show 40</option>
                    <option value={50}>Show 50</option>
                  </Input>
                </div>
              </Col>
            </Row>

            {/* 🔹 Table */}
            <Table bordered hover responsive>
              <thead className="bg-blue text-white">
                <tr>
                  <th className="text-center">#</th>
                  <th>Employee Name</th>
                  <th>Email Address</th>
                  <th>Contact Number</th>
                  <th>Query</th>
                  <th>Posted At</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No queries found.
                    </td>
                  </tr>
                ) : (
                  currentData.map((query, index) => (
                    <tr key={query.id}>
                      <td className="text-center">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="d-flex align-items-center">
                        <img
                          src={
                            query.employee?.profilePicture
                              ? `${process.env.REACT_APP_IMAGE_PATH}${query.employee.profilePicture}`
                              : "https://ui-avatars.com/api/?name=" +
                                encodeURIComponent(
                                  query.employee?.name || "User"
                                ) +
                                "&background=random"
                          }
                          alt={query.employee?.name}
                          className="rounded-circle me-2"
                          width="35"
                          height="35"
                          style={{ objectFit: "cover" }}
                        />
                        {query.employee?.name}
                      </td>
                      <td>{query.employee?.email}</td>
                      <td>{query.employee?.phone || "N/A"}</td>
                      <td>{query.title}</td>
                      <td>
                        {moment(query.createdAt).format("DD/MM/YYYY | hh:mm A")}
                      </td>
                      <td className="text-center">
                        <Badge
                          color={
                            query?.isReopened
                              ? "info"
                              : query.status
                              ? "success"
                              : "warning"
                          }
                          style={{ fontSize: "12px", padding: "8px 10px" }}
                        >
                          {query?.isReopened
                            ? "Re-open"
                            : query.status
                            ? "Closed"
                            : "Open"}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            id={`view-btn-${query.id}`}
                            className="btn-blue"
                            size="sm"
                            onClick={() => toggleModal(query)}
                          >
                            <FaEye /> View
                          </Button>
                          <UncontrolledTooltip target={`view-btn-${query.id}`}>
                            View Details
                          </UncontrolledTooltip>

                          <div style={{ position: "relative", display: "inline-block" }}>
                            <Button
                              id={`chat-btn-${query.id}`}
                              className="btn-blue"
                              size="xl"
                              onClick={() => toggleChatModal(query)}
                            >
                              <FaComments />
                            </Button>
                            {query.unreadCount > 0 && (
                              <span
                                className="badge rounded-pill bg-danger"
                                style={{
                                  position: "absolute",
                                  top: -6,
                                  right: -6,
                                  fontSize: 10,
                                  minWidth: 18,
                                  height: 18,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "0 4px",
                                }}
                              >
                                {query.unreadCount > 9 ? "9+" : query.unreadCount}
                              </span>
                            )}
                          </div>
                          <UncontrolledTooltip target={`chat-btn-${query.id}`}>
                            Chat Now
                          </UncontrolledTooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* 🔹 Pagination Controls */}
            <div className="d-flex justify-content-between  gap-2">
              <p className="mb-0">
                Showing {currentData.length} out of {queries.length} Queries.
              </p>
              <div>
                <Button
                  size="sm"
                  disabled={currentPage === 1}
                  className="fs-6"
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  <FaChevronCircleLeft />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    className="mx-2 fs-6"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  className="fs-6"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  <FaChevronCircleRight />
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* 🔹 Details Modal */}
        {selectedQuery && (
          <Modal
            isOpen={modalOpen}
            toggle={() => toggleModal()}
            size="lg"
            centered
          >
            <ModalHeader toggle={() => toggleModal()}>
              Query Details
              <Badge
                color={selectedQuery.status ? "success" : "warning"}
                className="ms-2"
              >
                {selectedQuery.status ? "Closed" : "Open"}
              </Badge>
            </ModalHeader>
            <ModalBody
              style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
            >
              <Table bordered className="border-secondary">
                <tbody>
                  <tr>
                    <th style={{ width: "30%" }}>Employee Name</th>
                    <td>{selectedQuery.employee?.name}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{selectedQuery.employee?.email}</td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td>{selectedQuery.employee?.phone || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Role</th>
                    <td>
                      <Badge color="info p-2">
                        {selectedQuery.employee?.role?.roleName || "N/A"}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Title</th>
                    <td>{selectedQuery.title}</td>
                  </tr>
                  <tr>
                    <th>Description</th>
                    <td>{selectedQuery.description}</td>
                  </tr>
                  <tr>
                    <th>Posted On</th>
                    <td>
                      {moment(selectedQuery.createdAt).format(
                        "DD/MM/YYYY | hh:mm A"
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </ModalBody>
            <ModalFooter>
              {!selectedQuery.status ? (
                <>
                  {checkAccess(7, 4) && (
                    <Button
                      color="success"
                      onClick={() => handleCloseQuery(selectedQuery.id)}
                    >
                      Close Query
                    </Button>
                  )}
                  <Button color="secondary" onClick={() => toggleModal()}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {checkAccess(7, 4) && (
                    <Button
                      color="danger"
                      onClick={() => handleReopenQuery(selectedQuery.id)}
                    >
                      Reopen Query
                    </Button>
                  )}
                  <Button color="secondary" onClick={() => toggleModal()}>
                    Cancel
                  </Button>
                </>
              )}
            </ModalFooter>
          </Modal>
        )}

        {/* 🔹 Chat Modal */}
        {chatQuery && (
          <Modal
            isOpen={chatModalOpen}
            toggle={() => toggleChatModal()}
            size="lg"
            centered
          >
            <ModalHeader
              toggle={() => toggleChatModal()}
              style={{ paddingBottom: 8 }}
            >
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-semibold">{chatQuery.title}</span>
                  <Badge
                    color={chatQuery.status ? "success" : "warning"}
                    style={{ fontSize: 11 }}
                  >
                    {chatQuery.status ? "Closed" : "Open"}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <img
                    src={
                      chatQuery.employee?.profilePicture
                        ? `${process.env.REACT_APP_IMAGE_PATH}${chatQuery.employee.profilePicture}`
                        : "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(
                            chatQuery.employee?.name || "User"
                          ) +
                          "&background=random"
                    }
                    alt={chatQuery.employee?.name}
                    className="rounded-circle"
                    width="24"
                    height="24"
                    style={{ objectFit: "cover" }}
                  />
                  <span className="text-muted" style={{ fontSize: 13 }}>
                    {chatQuery.employee?.name}
                  </span>
                </div>
              </div>
            </ModalHeader>
            <ModalBody style={{ padding: 0 }}>
              <div
                style={{
                  height: 420,
                  overflowY: "auto",
                  padding: "16px",
                  background: "#f4f6fb",
                }}
              >
                {messagesLoading ? (
                  <div className="text-center text-muted py-4">
                    Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No messages yet. Start the conversation.
                  </div>
                ) : (
                  messages.map(msg => {
                    const isMine =
                      Number(msg.senderId) === Number(adminUser?.id)
                    return (
                      <div
                        key={msg.id}
                        className={`d-flex mb-3 ${
                          isMine
                            ? "justify-content-end"
                            : "justify-content-start"
                        }`}
                      >
                        <div
                          className="p-2 px-3"
                          style={{
                            maxWidth: "72%",
                            borderRadius: 14,
                            backgroundColor: isMine ? "#3d6eb5" : "#fff",
                            color: isMine ? "#fff" : "#000",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                          }}
                        >
                          {!isMine && (
                            <div
                              className="fw-semibold"
                              style={{ fontSize: 12, color: "#3d6eb5" }}
                            >
                              {msg.sender?.name || "Employee"}
                            </div>
                          )}
                          <div style={{ fontSize: 14 }}>{msg.message}</div>
                          <div
                            className="text-end mt-1"
                            style={{
                              fontSize: 10,
                              opacity: 0.75,
                            }}
                          >
                            {moment(msg.createdAt).format("DD/MM/YYYY hh:mm A")}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
              <div className="d-flex gap-2 p-3 border-top bg-white">
                <Input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                />
                <Button
                  className="btn-blue"
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                >
                  <FaPaperPlane />
                </Button>
              </div>
            </ModalBody>
          </Modal>
        )}
      </PermittedComponent>
    </div>
  )
}

export default UserQuery
